import React, { useState, useEffect, useRef } from "react";
import { ChevronRight, Clock, ArrowLeft, CheckCircle, AlertTriangle } from "lucide-react";
import SharedNavbar from "../../components/SharedNavbar";

const APP_URL = "";

function useFadeIn(threshold = 0.1) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisible(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

function FadeIn({ children, delay = 0, className = "" }) {
  const [ref, visible] = useFadeIn();
  return (
    <div ref={ref} className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(20px)",
        transition: `opacity 0.55s ease ${delay}ms, transform 0.55s ease ${delay}ms`,
      }}
    >{children}</div>
  );
}

function InlineCTA() {
  return (
    <div
      className="rounded-2xl p-7 my-10 text-center"
      style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)", boxShadow: "0 12px 40px rgba(29,78,216,0.18)" }}
    >
      <p className="text-white font-extrabold text-xl mb-2">Stop Calculating Manually.</p>
      <p className="text-slate-300 text-sm mb-5">ONE CLICK is all it takes — The Fastest RVU Tracker. <span className="text-blue-400 font-semibold">PERIOD.</span></p>
      <a
        href={`${APP_URL}/register`}
        data-testid="article-inline-cta"
        className="inline-flex items-center gap-2 text-sm font-bold text-white rounded-full px-7 py-3 transition-all hover:-translate-y-px hover:shadow-xl"
        style={{ background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)", boxShadow: "0 4px 16px rgba(37,99,235,0.40)" }}
      >
        Try Doc RVU Tracker Free <ChevronRight size={15} />
      </a>
    </div>
  );
}

const ARTICLES_META = [
  { slug: "/rvu-articles/how-to-track-rvus", title: "How to Track RVUs: Complete Guide for Providers", desc: "Learn what RVUs are, why tracking matters, common mistakes, and the most effective approach to stay on top of your productivity.", category: "RVU Fundamentals", readTime: "6 min", badge: "bg-blue-50 text-blue-700 border-blue-200", img: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwxfHxkb2N0b3IlMjBwcm92aWRlciUyMG1lZGljYWwlMjB0YWJsZXQlMjBkYXRhJTIwdHJhY2tpbmclMjBjbGluaWNhbHxlbnwwfHx8fDE3NzMzNjQ5Mzh8MA&ixlib=rb-4.1.0&q=85" },
  { slug: "/rvu-articles/missed-billing-codes", title: "Most Commonly Missed Billing Codes", desc: "Discover the CPT codes most frequently missed in urgent care, primary care, and the ER — and how to avoid lost income.", category: "Billing & Coding", readTime: "10 min", badge: "bg-orange-50 text-orange-700 border-orange-200", img: "https://images.unsplash.com/photo-1710503913397-df9bfea7e50b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwyfHxtZWRpY2FsJTIwYmlsbGluZyUyMGNvZGluZyUyMGhvc3BpdGFsJTIwY2hhcnQlMjBwYXBlcndvcmt8ZW58MHx8fHwxNzczNDM5ODM1fDA&ixlib=rb-4.1.0&q=85" },
  { slug: "/rvu-articles/rvus-and-paycheck", title: "How RVUs Translate Into Your Paycheck", desc: "Understand how wRVUs directly convert to compensation, what models exist, and how to take control of your earnings.", category: "RVU & Compensation", readTime: "8 min", badge: "bg-green-50 text-green-700 border-green-200", img: "https://images.unsplash.com/photo-1681505526188-b05e68c77582?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwyfHxwaHlzaWNpYW4lMjBjb250cmFjdCUyMG5lZ290aWF0aW9uJTIwaW5jb21lJTIwZmluYW5jaWFsJTIwcGxhbm5pbmclMjBwcm9mZXNzaW9uYWx8ZW58MHx8fHwxNzczNTE0OTQ3fDA&ixlib=rb-4.1.0&q=85" },
];

function TableOfContents({ items }) {
  return (
    <nav className="rounded-2xl border border-slate-200 bg-slate-50 p-5 mb-10" aria-label="Table of contents">
      <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3">In This Article</p>
      <ol className="space-y-1.5 list-none p-0 m-0">
        {items.map((item, i) => (
          <li key={item.id}>
            <a href={`#${item.id}`} className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 font-medium hover:underline transition-colors">
              <span className="text-slate-400 text-xs min-w-[1.25rem]">{i + 1}.</span>
              {item.label}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}

function RelatedArticles({ currentSlug }) {
  const related = ARTICLES_META.filter((a) => a.slug !== currentSlug);
  return (
    <section className="mb-10 mt-2" aria-label="Related articles">
      <h2 className="text-2xl font-bold text-slate-900 mb-5 tracking-tight">Continue Reading</h2>
      <div className="grid sm:grid-cols-2 gap-4">
        {related.map((article) => (
          <a key={article.slug} href={article.slug} className="group block rounded-2xl border border-slate-200 bg-white overflow-hidden hover:border-blue-200 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 no-underline">
            <div className="aspect-[16/9] overflow-hidden">
              <img src={article.img} alt={article.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            </div>
            <div className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full border ${article.badge}`}>{article.category}</span>
                <span className="text-xs text-slate-400">{article.readTime} read</span>
              </div>
              <h3 className="text-base font-bold text-slate-800 mb-2 group-hover:text-blue-600 transition-colors leading-snug">{article.title}</h3>
              <p className="text-slate-500 text-sm leading-relaxed">{article.desc}</p>
              <div className="mt-3 flex items-center gap-1 text-blue-600 text-sm font-semibold">Read Article <ChevronRight size={13} /></div>
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}

export default function HowToTrackRVUs() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "How to Track RVUs: Complete Guide for Physicians & Providers | Doc RVU Tracker";
    const managed = [];
    function setMeta(selector, attr, val, content) {
      let el = document.querySelector(selector);
      const existed = !!el;
      const prevContent = existed ? el.getAttribute("content") : null;
      if (!existed) { el = document.createElement("meta"); document.head.appendChild(el); }
      el.setAttribute(attr, val); el.setAttribute("content", content);
      managed.push({ el, existed, prevContent });
    }
    setMeta('meta[name="description"]', "name", "description", "Learn how to track RVUs accurately in 2026. A complete guide for physicians, NPs, and PAs — covering what RVUs are, why tracking matters, common mistakes, and the best tools to maximize your productivity.");
    setMeta('meta[name="keywords"]', "name", "keywords", "how to track RVUs, RVU tracking for physicians, physician RVU tracker, track work RVUs, wRVU productivity tracking, RVU guide 2026");
    setMeta('meta[property="og:title"]', "property", "og:title", "How to Track RVUs: Complete Guide for Physicians & Providers");
    setMeta('meta[property="og:description"]', "property", "og:description", "A complete guide on how to track RVUs for physicians, NPs, and PAs. Learn what RVUs are, why tracking matters, common mistakes, and the best tools.");
    setMeta('meta[property="og:type"]', "property", "og:type", "article");
    setMeta('meta[property="og:url"]', "property", "og:url", `${window.location.origin}/rvu-articles/how-to-track-rvus`);
    setMeta('meta[property="og:image"]', "property", "og:image", "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=1200&q=80");
    setMeta('meta[property="og:site_name"]', "property", "og:site_name", "Doc RVU Tracker");
    setMeta('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");
    setMeta('meta[name="twitter:title"]', "name", "twitter:title", "How to Track RVUs: Complete Guide for Physicians & Providers");
    setMeta('meta[name="twitter:description"]', "name", "twitter:description", "A complete guide on RVU tracking for physicians, NPs, and PAs. Learn what RVUs are, why tracking matters, and the best approach.");
    setMeta('meta[name="twitter:image"]', "name", "twitter:image", "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=1200&q=80");
    let canonical = document.querySelector('link[rel="canonical"]');
    const canonicalExisted = !!canonical;
    const prevHref = canonicalExisted ? canonical.getAttribute("href") : null;
    if (!canonical) { canonical = document.createElement("link"); canonical.setAttribute("rel", "canonical"); document.head.appendChild(canonical); }
    canonical.setAttribute("href", `${window.location.origin}/rvu-articles/how-to-track-rvus`);
    const jsonLd = document.createElement("script");
    jsonLd.type = "application/ld+json";
    jsonLd.text = JSON.stringify({ "@context": "https://schema.org", "@type": "Article", "headline": "How to Track RVUs: Complete Guide for Physicians & Providers", "description": "Learn how to track RVUs accurately in 2026. A complete guide for physicians, NPs, and PAs on RVU tracking, common mistakes, and tools to maximize productivity.", "author": { "@type": "Organization", "name": "Doc RVU Tracker", "url": window.location.origin }, "publisher": { "@type": "Organization", "name": "Doc RVU Tracker", "url": window.location.origin, "logo": { "@type": "ImageObject", "url": `${window.location.origin}/rvu-logo.png` } }, "datePublished": "2026-03-01", "dateModified": "2026-03-01", "image": "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=1200&q=80", "url": `${window.location.origin}/rvu-articles/how-to-track-rvus`, "mainEntityOfPage": { "@type": "WebPage", "@id": `${window.location.origin}/rvu-articles/how-to-track-rvus` } });
    document.head.appendChild(jsonLd);
    return () => {
      document.title = prevTitle;
      managed.forEach(({ el, existed, prevContent }) => { if (existed) el.setAttribute("content", prevContent); else el.parentNode && el.parentNode.removeChild(el); });
      if (canonicalExisted) canonical.setAttribute("href", prevHref); else canonical.parentNode && canonical.parentNode.removeChild(canonical);
      jsonLd.parentNode && jsonLd.parentNode.removeChild(jsonLd);
    };
  }, []);

  return (
    <div className="min-h-screen font-sans antialiased" style={{ fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif", background: "#f8fafc" }}>
      <SharedNavbar activePage="articles" />

      {/* Article Header */}
      <section className="pt-28 pb-0 px-4" style={{ background: "linear-gradient(160deg, #f0f7ff 0%, #f8fafc 100%)" }}>
        <div className="max-w-3xl mx-auto">
          <a href="/rvu-articles" className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 mb-6 transition-colors" data-testid="back-to-articles">
            <ArrowLeft size={15} /> Back to Articles
          </a>
          <div className="flex items-center gap-3 mb-4">
            <span className="text-xs font-semibold px-3 py-1 rounded-full border bg-blue-50 text-blue-700 border-blue-200">RVU Fundamentals</span>
            <span className="flex items-center gap-1 text-xs text-slate-400"><Clock size={11} /> 6 min read</span>
            <span className="text-xs text-slate-400">March 2026</span>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-slate-900 tracking-tight leading-tight mb-6">
            How to Track RVUs<br className="hidden sm:block" />
            <span style={{ background: "linear-gradient(135deg, #2563eb 0%, #0ea5e9 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}> (Complete Guide for Providers)</span>
          </h1>
        </div>
      </section>

      {/* Hero image */}
      <div className="px-4 pb-10" style={{ background: "linear-gradient(160deg, #f0f7ff 0%, #f8fafc 100%)" }}>
        <div className="max-w-3xl mx-auto">
          <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-md" style={{ maxHeight: 380 }}>
            <img
              src="https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwxfHxkb2N0b3IlMjBwcm92aWRlciUyMG1lZGljYWwlMjB0YWJsZXQlMjBkYXRhJTIwdHJhY2tpbmclMjBjbGluaWNhbHxlbnwwfHx8fDE3NzMzNjQ5Mzh8MA&ixlib=rb-4.1.0&q=85"
              alt="Physician tracking RVUs and patient encounters on a mobile device"
              loading="lazy"
              className="w-full object-cover"
              style={{ height: 380, objectPosition: "center top" }}
            />
          </div>
        </div>
      </div>

      {/* Article Body */}
      <article className="px-4 pb-16">
        <div className="max-w-3xl mx-auto">

          {/* Intro */}
          <FadeIn>
            <div className="prose-custom mb-10">
              <p className="text-lg text-slate-700 leading-relaxed mb-4">
                Tracking Relative Value Units (RVUs) is essential for modern healthcare providers working in productivity-based compensation models. However, many providers still rely on spreadsheets or delayed employer reports.
              </p>
              <p className="text-lg text-slate-700 leading-relaxed">
                This guide explains how RVUs work, why they matter, and how tracking them accurately can directly impact your income and long-term career growth.
              </p>
            </div>
          </FadeIn>

          {/* Table of Contents */}
          <FadeIn delay={30}>
            <TableOfContents items={[
              { id: "what-are-rvus", label: "What Are RVUs?" },
              { id: "why-tracking-matters", label: "Why Tracking RVUs Matters" },
              { id: "common-mistakes", label: "Common RVU Tracking Mistakes" },
              { id: "best-way-to-track", label: "Best Way to Track RVUs" },
              { id: "free-rvu-calculator", label: "Free RVU Calculator" },
              { id: "final-thoughts", label: "Final Thoughts" },
            ]} />
          </FadeIn>

          {/* Section: What Are RVUs */}
          <FadeIn delay={60}>
            <section id="what-are-rvus" className="mb-10 scroll-mt-24">
              <h2 className="text-3xl font-bold text-slate-900 mb-4 tracking-tight border-l-4 border-blue-500 pl-4">What Are RVUs?</h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                Relative Value Units (RVUs) are a standardized metric used across the U.S. healthcare system to measure the value of clinical services based on complexity, time, and resources required.
              </p>
              <p className="text-slate-700 leading-relaxed mb-5">
                Each CPT code is assigned a specific RVU value. The more complex the service, the higher the RVU.
              </p>
              <p className="text-slate-700 font-medium mb-3">RVUs are composed of three components:</p>
              <div className="space-y-3">
                {[
                  { label: "Work RVUs (wRVUs)", desc: "reflect the provider's time, skill, effort, and clinical decision-making", color: "blue" },
                  { label: "Practice Expense RVUs (peRVUs)", desc: "reflect overhead costs such as staff, equipment, and supplies", color: "teal" },
                  { label: "Malpractice RVUs (mpRVUs)", desc: "reflect professional liability costs", color: "purple" },
                ].map(({ label, desc, color }) => (
                  <div key={label} className={`flex items-start gap-3 bg-${color}-50 border border-${color}-100 rounded-xl px-4 py-3`}>
                    <CheckCircle size={16} className={`text-${color}-500 flex-shrink-0 mt-0.5`} />
                    <p className="text-slate-700 text-sm leading-relaxed"><strong className="text-slate-900">{label}</strong> — {desc}</p>
                  </div>
                ))}
              </div>
              <p className="text-slate-600 text-base mt-4 p-4 bg-blue-50 border border-blue-100 rounded-xl">
                For most providers, <strong className="text-slate-800">wRVUs are the most important component</strong>, as they are commonly used to determine productivity and compensation.
              </p>
            </section>
          </FadeIn>

          {/* Section: Why Tracking RVUs Matters */}
          <FadeIn delay={60}>
            <section id="why-tracking-matters" className="mb-10 scroll-mt-24">
              <h2 className="text-3xl font-bold text-slate-900 mb-4 tracking-tight border-l-4 border-blue-500 pl-4">Why Tracking RVUs Matters</h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                RVUs directly influence both how providers are paid and how practices generate revenue.
              </p>
              <p className="text-slate-700 leading-relaxed mb-5">For productivity-based providers, RVUs often determine bonus structures, compensation tiers, performance evaluations, and contract negotiations.</p>
              <p className="text-slate-700 leading-relaxed mb-2">At the practice level, RVUs determine how much reimbursement is received from payers. In simple terms:</p>
              <div className="bg-green-50 border border-green-200 rounded-xl px-5 py-4 mb-5 flex items-center gap-3">
                <span className="text-green-700 font-bold text-base">More RVUs generated = more revenue for the practice.</span>
              </div>
              <p className="text-slate-700 leading-relaxed mb-5">
                Even for providers who are paid hourly or on salary, RVU productivity still matters. Higher RVU production demonstrates financial value to the organization and can strengthen leverage when negotiating higher hourly rates, salary increases, productivity bonuses, and even leadership opportunities.
              </p>
              <p className="text-slate-700 leading-relaxed mb-5">
                Providers who track their RVUs consistently gain the ability to understand their real-time productivity, forecast income more accurately, identify underbilling, improve workflow efficiency, and make a stronger case when advocating for fair compensation.
              </p>
              <p className="text-slate-600 text-base italic p-4 bg-slate-50 border border-slate-200 rounded-xl">
                Without tracking, many clinicians underestimate their production and potential earning power.
              </p>
            </section>
          </FadeIn>

          {/* Inline CTA after Why section */}
          <FadeIn delay={60}><InlineCTA /></FadeIn>

          {/* Section: Common Mistakes */}
          <FadeIn delay={60}>
            <section id="common-mistakes" className="mb-10 scroll-mt-24">
              <h2 className="text-3xl font-bold text-slate-900 mb-4 tracking-tight border-l-4 border-red-400 pl-4">Common RVU Tracking Mistakes</h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                Many providers unintentionally reduce their productivity insights by:
              </p>
              <div className="space-y-3">
                {[
                  "Waiting for delayed monthly productivity reports",
                  "Using outdated or inaccurate spreadsheets",
                  "Forgetting to log procedures",
                  "Miscalculating conversion factors",
                  "Underestimating their clinical output",
                ].map((item) => (
                  <div key={item} className="flex items-start gap-3 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
                    <AlertTriangle size={15} className="text-red-400 flex-shrink-0 mt-0.5" />
                    <p className="text-slate-700 text-sm">{item}</p>
                  </div>
                ))}
              </div>
              <p className="text-slate-600 text-sm mt-4 p-4 bg-red-50 border border-red-100 rounded-xl font-medium text-red-700">
                These mistakes can result in lost income and poor visibility into performance trends.
              </p>
            </section>
          </FadeIn>

          {/* Section: Best Way to Track */}
          <FadeIn delay={60}>
            <section id="best-way-to-track" className="mb-10 scroll-mt-24">
              <h2 className="text-3xl font-bold text-slate-900 mb-4 tracking-tight border-l-4 border-blue-500 pl-4">Best Way to Track RVUs</h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                The most effective way to track RVUs is through real-time logging using a dedicated productivity tracking system.
              </p>
              <p className="text-slate-700 leading-relaxed mb-5">Modern RVU tracking tools allow providers to log CPT codes instantly, monitor productivity trends, calculate compensation automatically, improve billing accuracy, and identify workflow inefficiencies — all without slowing down clinical workflow.</p>
              <p className="text-slate-600 text-base mb-5 italic">
                Manual tracking methods are prone to error, delay, and inconsistency.
              </p>
              <p className="text-slate-700 leading-relaxed mb-3">
                If you want the fastest and easiest way to track your productivity, <strong className="text-slate-900">Doc RVU Tracker</strong> is designed specifically for real clinical workflows. It allows providers to track RVUs instantly with just one click, understand productivity trends, and potentially uncover thousands of dollars in previously unrecognized production — all for just a small monthly subscription.
              </p>
              <p className="text-slate-600 text-base p-4 bg-blue-50 border border-blue-100 rounded-xl">
                Want to see it in action?{" "}
                <a
                  href="/register"
                  data-testid="article-docrvutracker-link"
                  className="font-semibold text-blue-600 hover:text-blue-700 hover:underline transition-colors"
                >
                  Start tracking your RVUs with Doc RVU Tracker →
                </a>
              </p>
            </section>
          </FadeIn>

          {/* Section: Free RVU Calculator */}
          <FadeIn delay={60}>
            <section id="free-rvu-calculator" className="mb-10 scroll-mt-24">
              <h2 className="text-3xl font-bold text-slate-900 mb-4 tracking-tight border-l-4 border-blue-500 pl-4">Free RVU Calculator</h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                If you want to quickly estimate your productivity, you can use our free RVU calculator:
              </p>
              <a
                href="/free-rvu-calculator"
                data-testid="article-free-calc-link"
                className="inline-flex items-center gap-2 text-base font-bold text-white rounded-full px-7 py-3 transition-all hover:-translate-y-px hover:shadow-lg"
                style={{ background: "linear-gradient(135deg, #2563eb 0%, #0ea5e9 100%)", boxShadow: "0 4px 16px rgba(37,99,235,0.35)" }}
              >
                Free wRVU Calculator <ChevronRight size={16} />
              </a>
            </section>
          </FadeIn>

          {/* Section: Final Thoughts */}
          <FadeIn delay={60}>
            <section id="final-thoughts" className="mb-10 scroll-mt-24">
              <h2 className="text-3xl font-bold text-slate-900 mb-4 tracking-tight border-l-4 border-blue-500 pl-4">Final Thoughts</h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                RVU tracking is no longer optional for providers working in modern healthcare systems. Understanding and monitoring your productivity gives you control over your income, performance, and career trajectory.
              </p>
              <p className="text-slate-700 leading-relaxed mb-4">
                Rather than relying on delayed reports or manual spreadsheets, using a purpose-built tracking system can help you stay ahead of your productivity goals.
              </p>
              <p className="text-slate-700 leading-relaxed">
                If you're ready to take full control of your RVU performance, start tracking today with the Fastest and Easiest RVU Tracker available.
              </p>
            </section>
          </FadeIn>

          {/* Related Articles */}
          <FadeIn delay={60}>
            <RelatedArticles currentSlug="/rvu-articles/how-to-track-rvus" />
          </FadeIn>

          {/* Final CTA */}
          <FadeIn delay={60}>
            <div
              className="rounded-2xl p-8 text-center mt-4"
              style={{ background: "linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 50%, #0369a1 100%)", boxShadow: "0 20px 60px rgba(29,78,216,0.25)" }}
            >
              <img src="/rvu-logo.png" alt="Doc RVU Tracker" style={{ width: 56, height: 56, borderRadius: "14px", objectFit: "cover", boxShadow: "0 4px 16px rgba(22,163,74,0.40)", margin: "0 auto 16px" }} />
              <h3 className="text-2xl font-extrabold text-white mb-2 tracking-tight">Try it now.</h3>
              <p className="text-blue-100 text-base mb-6">Track your RVUs with ONE CLICK. The Fastest RVU Tracker. PERIOD.</p>
              <a
                href={`${APP_URL}/register`}
                data-testid="article-final-cta"
                className="inline-flex items-center gap-2 text-base font-bold text-white rounded-full px-8 py-3.5 transition-all hover:-translate-y-px hover:shadow-xl"
                style={{ background: "rgba(255,255,255,0.15)", border: "2px solid rgba(255,255,255,0.40)" }}
              >
                Get Started at DocRVUTracker.com <ChevronRight size={18} />
              </a>
            </div>
          </FadeIn>

        </div>
      </article>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-10 px-4">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-5">
          <div>
            <div className="flex items-center gap-2.5 mb-1.5">
              <img src="/rvu-logo.png" alt="Doc RVU Tracker" style={{ width: 30, height: 30, borderRadius: "7px", objectFit: "cover" }} />
              <span className="text-white font-semibold text-sm">Doc RVU Tracker</span>
            </div>
            <p className="text-xs text-slate-500">The One-Click tracker built for providers by providers.</p>
            <p className="text-xs text-slate-600">© {new Date().getFullYear()} Doc RVU Tracker. All rights reserved.</p>
          </div>
          <div className="flex flex-col items-center md:items-end gap-1">
            <div className="flex gap-5 text-sm">
              <a href="/" className="hover:text-white transition-colors">Home</a>
              <a href="/rvu-articles" className="hover:text-white transition-colors">Articles</a>
              <a href="/free-rvu-calculator" className="hover:text-white transition-colors">Free Calculator</a>
              <a href={`${APP_URL}/login`} className="hover:text-white transition-colors">Log In</a>
            </div>
            <p className="text-sm font-medium text-slate-300 mt-1">DocRVUTracker@gmail.com</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
